import CircularProgress from '@mui/material/CircularProgress'
import React from 'react'

import ButtonStyled from './styled/ButtonStyled'
import LinkStyled from './styled/LinkStyled'

import { SxProps, Theme } from '@mui/material/styles'
import { createHtmlAttrId } from '../helpers'

export interface ButtonProps {
  children?: React.ReactNode
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  typestyle?: 'primary' | 'secondary' | 'tertiary' | 'accent'
  size?: 'big' | 'small' | 'mobile'
  disabled?: boolean
  fullWidth?: boolean
  async?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>
  type?: 'button' | 'submit' | 'reset'
  href?: string
  loading?: boolean
  sx?: SxProps<Theme>
}

export const Button: React.FC<React.PropsWithChildren<ButtonProps>> = ({
  async,
  children,
  startIcon,
  endIcon,
  size,
  href,
  disabled,
  fullWidth,
  loading,
  sx,
  ...props
}) => {
  const typestyle = props.typestyle || 'primary'

  const [isLoadingState, setIsLoadingState] = React.useState<boolean>(false)
  const [isMount, setIsMount] = React.useState<boolean>(true)

  React.useEffect(() => {
    return () => {
      setIsMount(false)
    }
  }, [])

  const onClick = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    if (!props.onClick) return
    if (async) {
      setIsLoadingState(true)
      try {
        await props.onClick(e)
      } finally {
        if (isMount) setIsLoadingState(false)
      }
    } else {
      await props.onClick(e)
    }
  }

  const isLoading = isLoadingState || !!loading

  const elementId = createHtmlAttrId(`${typestyle}-${children as string}`)

  const buttonComponent = (
    <ButtonStyled
      id={`button-${elementId}`}
      startIcon={startIcon}
      endIcon={endIcon}
      variant="outlined"
      typestyle={typestyle}
      typesize={size || 'big'}
      className={isLoading ? 'loading' : ''}
      type={props.type}
      fullWidth={fullWidth}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => void onClick(e)}
      disabled={isLoading || disabled}
      disableRipple
      sx={sx}
    >
      <div className="button-content">{children}</div>
      {isLoading && (
        <div className="loading-content">
          <CircularProgress size={14} />
        </div>
      )}
    </ButtonStyled>
  )
  if (href) return <LinkStyled to={href}>{buttonComponent}</LinkStyled>
  return buttonComponent
}
